import React, { useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconVolume3 } from '@tabler/icons-react'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'

import {
  toggleMediaPlayerPlaying,
  updateMediaPlayer,
  resetMediaPlayer,
  pauseMediaPlayer,
  updateSettings,
} from '@/reducers/media/media.redux'
import { fetchMediaForCallId, setHardSelectedEvent } from '@/reducers/media/media.actions'
import {
  audioErrorSwitch,
  getBooleanValueFromString,
  getClosestTranscriptBlock,
} from '@/utils/helpers'
import { AUDIO_EXPIRATION_BUFFER, SKIP_MODIFIER } from '@/utils/constants'
import { Transcript } from '@/components/transcript/Transcript'
import { useTranscript } from '@/components/transcript/TranscriptContext'
import { Banner } from '@/components/banners/Banner'
import { parseCallExplorerParams } from '@/views/Calls/components/helpers'

import VideoPlayer from './VideoPlayer'
import { AudioPlayer } from './AudioPlayer'
import { useAudioPlayer } from './AudioPlayerContext'
import { useVideoPlayer } from './VideoPlayerContext'

import './MediaPlayer.scss'

export const MediaPlayer = ({
  isTranscriptAvailable = false,
  transcriptMetadata = {},
  transcriptEnableAutoScroll = false,
  showTranscriptSelector = false,
  showTranscriptButton = false,
  showTranslateFeature = false,
  hideShareButton = false,
  useMinimalUi = false,
  handleShowPlayer,
  handleHidePlayer,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const {
    audioRef,
    jumpAudioTo,
    playAudio,
    pauseAudio,
    changeAudioPlaybackRate,
    changeAudioVolume,
    addAudioEvent,
    removeAudioEvent,
    checkCorsSupport,
  } = useAudioPlayer()
  const {
    videoRef,
    jumpVideoTo,
    playVideo,
    pauseVideo,
    changeVideoPlaybackRate,
    addVideoEvent,
    removeVideoEvent,
    isVideoMaximized,
  } = useVideoPlayer()
  const {
    allowAutoScroll,
    isCommentingActive,
    isComposingComment,
    transcriptBlocksFirstTimes,
    transcriptType,
    closestTranscriptIndexRef,
    transcriptBlockRef,
    transcriptToDisplay,
    isTranscriptVisible,
    handleUpdateHighlightedTranscriptIndex,
    comments,
  } = useTranscript()
  const isTranscriptActive =
    isTranscriptAvailable && isTranscriptVisible && !isEmpty(transcriptToDisplay)
  const { loading, mediaPlayer, settings } = useSelector((state) => state.media)
  const {
    callId,
    progress,
    duration,
    audioStatus,
    audioUrl,
    audioUrlExpiration,
    audioError,
    isPlaying,
    videoError,
    videoStatus,
    videoUrls,
    hardSelectedEvent,
  } = mediaPlayer
  const { playbackRate, volume, skipSilence } = settings
  const trackProgressRef = useRef()
  const trackExpirationRef = useRef()
  const isAudioAvailable = !audioError && audioStatus === 'available'
  const isVideoAvailable = !videoError && videoStatus === 'available'
  // An open comment composer takes the player: the transport (play, skip,
  // volume, rate, scrub) goes inert until the comment is submitted or dropped.
  // Close/Share/Hide-transcript stay live, so the composer can't trap anyone
  // (AVR-875).
  const playerIsDisabled = !(isAudioAvailable || isVideoAvailable) || isComposingComment

  const handleScroll = () => {
    if (transcriptBlockRef.current?.childNodes?.[closestTranscriptIndexRef.current]) {
      const transcriptContainer = transcriptBlockRef.current
      transcriptContainer.scrollTo({
        top:
          transcriptBlockRef.current.childNodes[closestTranscriptIndexRef.current].offsetTop -
          transcriptContainer.offsetTop,
        behavior: 'smooth',
      })
    }
  }

  const handleUpdateClosestTranscriptBlock = (value) => {
    if (audioRef.current) {
      const closest = getClosestTranscriptBlock(value, transcriptBlocksFirstTimes)
      if (closestTranscriptIndexRef.current !== closest) {
        closestTranscriptIndexRef.current = closest
        handleUpdateHighlightedTranscriptIndex(closest)
      }
    }
  }

  const setIsPlaying = (value) => {
    if (isPlaying !== value) {
      dispatch(updateMediaPlayer({ isPlaying: value }))
    }
  }

  const setProgress = (value) => {
    // Every seek funnels through here — the scrub track, the skip buttons and
    // the sentiment graph, which draws its own clickable timeline outside the
    // disabled control row. One guard covers them all.
    if (isComposingComment) {
      return
    }
    dispatch(updateMediaPlayer({ progress: value }))

    if (isAudioAvailable) jumpAudioTo(value)
    if (isVideoAvailable) jumpVideoTo(value)
  }

  const setDuration = (value) => {
    dispatch(updateMediaPlayer({ duration: value }))
  }

  const setVolume = (value) => {
    dispatch(updateSettings({ volume: value }))
    changeAudioVolume(value)
  }

  const setSkipSilence = (value) => {
    dispatch(updateSettings({ skipSilence: value }))
  }

  const setPlaybackRate = (value) => {
    dispatch(updateSettings({ playbackRate: value }))

    if (isAudioAvailable) changeAudioPlaybackRate(value)
    if (isVideoAvailable) changeVideoPlaybackRate(value)
  }

  const setErrorState = (value) => {
    if (audioRef.current?.error) {
      audioErrorSwitch(audioRef.current.error.code, audioRef.current.error.message)
    }

    dispatch(updateMediaPlayer({ audioError: value }))
  }

  const togglePlayState = () => {
    // Belt to the disabled button's braces: the keyboard, a stale render or a
    // future caller must not start audio under an open composer either.
    if (isComposingComment) {
      return
    }
    dispatch(toggleMediaPlayerPlaying())
  }

  const getSkipSilence = (currentTime) => {
    if (skipSilence && !isEmpty(transcriptToDisplay)) {
      const currentSilence = transcriptToDisplay.filter((block) => {
        return (
          block.type === 'silence' &&
          currentTime >= block.timestamp &&
          currentTime <= block.timestamp + block.duration
        )
      })

      if (!isEmpty(currentSilence)) {
        const updatedTime = currentSilence[0].timestamp + currentSilence[0].duration

        return updatedTime
      }
    }
    return null
  }

  const stopExpirationTracking = () => {
    clearInterval(trackExpirationRef.current)
    trackExpirationRef.current = null
  }

  const startExpirationTracking = () => {
    // TODO: This has no video-only tracking
    if (audioUrlExpiration > 0) {
      const intervalValue = audioUrlExpiration * 1000 - AUDIO_EXPIRATION_BUFFER

      trackExpirationRef.current = setInterval(() => {
        dispatch(
          fetchMediaForCallId(
            callId,
            audioStatus === 'available',
            videoStatus === 'available',
            isPlaying
          )
        )
      }, intervalValue)
    }
  }

  const stopProgressTracking = () => {
    clearInterval(trackProgressRef.current)
    trackProgressRef.current = null
  }

  const startProgressTracking = () => {
    // Clear any timers already running
    stopProgressTracking()

    trackProgressRef.current = setInterval(() => {
      // Current time will use video if no audio exists
      let currentTime = isAudioAvailable
        ? audioRef.current.currentTime
        : videoRef.current.currentTime
      const skipTime = getSkipSilence(currentTime)

      if (!isNil(skipTime)) {
        setProgress(skipTime)
        currentTime = skipTime
      }

      const isEnded = isAudioAvailable ? audioRef.current.ended : videoRef.current.ended

      if (isEnded) {
        setProgress(duration)
        setIsPlaying(false)
      } else {
        dispatch(updateMediaPlayer({ progress: currentTime }))
      }
    }, [50])
  }

  const play = () => {
    if (isAudioAvailable) playAudio()
    if (isVideoAvailable) playVideo()

    startProgressTracking()
  }

  const pause = () => {
    if (isAudioAvailable) pauseAudio()
    if (isVideoAvailable) pauseVideo()

    stopProgressTracking()
  }

  const handleScrub = (event) => {
    const newTime = event.target.valueAsNumber

    setProgress(newTime)
  }

  const handleForwardSkip = () => {
    const newTime = progress + SKIP_MODIFIER
    const canScrubForward = newTime < duration

    if (canScrubForward) {
      setProgress(newTime)
    } else {
      setProgress(duration)
    }
  }

  const handleBackSkip = () => {
    const newTime = progress - SKIP_MODIFIER
    const canScrubBack = newTime > 0

    if (canScrubBack) {
      setProgress(newTime)
    } else {
      setProgress(0)
    }
  }

  const handlePlaybackRateChange = (value) => {
    const updatedPlaybackRate = parseFloat(value)

    localStorage.setItem('audioPlaybackRate', updatedPlaybackRate)
    setPlaybackRate(updatedPlaybackRate)
  }

  const handleVolumeChange = (value) => {
    setVolume(value)
  }

  const handleSkipSilenceChange = (value) => {
    localStorage.setItem('skipSilence', value)

    dispatch(updateSettings({ skipSilence: value }))
  }

  const handleTimestampClick = (timestamp) => {
    if (isComposingComment) {
      return
    }
    dispatch(updateMediaPlayer({ isPlaying: true, hardSelectedEvent: { timestamp } }))
  }

  const handleCloseDrawer = () => {
    if (handleHidePlayer) {
      handleHidePlayer()
    }

    pause()
    dispatch(resetMediaPlayer())
  }

  const videoOnPlaying = () => {
    setIsPlaying(true)
  }

  const videoOnPause = () => {
    setIsPlaying(false)
  }

  const videoOnSeeked = (event) => {
    const { currentTime } = event.target
    const isFullscreen = document.fullscreenElement === videoRef.current
    const isPip = document.pictureInPictureElement === videoRef.current
    const isNewEvent = currentTime !== progress

    if (isNewEvent && (isFullscreen || isPip)) {
      dispatch(setHardSelectedEvent({ timestamp: currentTime, ignoreVideo: true }))
    }
  }

  useEffect(() => {
    if (isPlaying && duration !== 0) {
      if (handleShowPlayer) {
        handleShowPlayer()
      }
      if (progress === duration) {
        // Set the progress to the beginning if you try to play after the audio has ended.
        // This ensures it doesn't just start for a few milliseconds, go to the beginning, then pause.
        setProgress(0)
      }
      play()
    }

    // Pause if audio is loaded but not playing
    if (!isPlaying) {
      pause()
    }
  }, [isPlaying, duration, skipSilence])

  useEffect(() => {
    const newTime = hardSelectedEvent?.timestamp

    // There is no timestamp on render therefore requiring a value check here
    if (newTime !== undefined) {
      dispatch(updateMediaPlayer({ progress: newTime }))

      if (isAudioAvailable) {
        jumpAudioTo(newTime)
      }

      // This was causing an infinite loop in TranscriptViewer -> onSeeked, because it would jumpTo the video,
      // which would seek, which would hit this event, and repeat. If we ignore this jumpTo, it will just update
      // the audio progress and let the full screen seek handle itself
      if (isVideoAvailable && !hardSelectedEvent.ignoreVideo) {
        jumpVideoTo(newTime)
      }
    }

    // if an event is selected, scroll the container to show the trigger and surrounding transcript context.
    if (hardSelectedEvent && audioRef.current) {
      handleUpdateClosestTranscriptBlock(audioRef.current.currentTime)
    }
  }, [hardSelectedEvent])

  useEffect(() => {
    const durationListener = (event) => {
      setDuration(event.target.duration)
    }
    const errorListener = () => {
      setErrorState(true)
    }
    const restorePlaybackRate = () => {
      // Browsers reset playbackRate to 1 when a new source loads. localStorage holds
      // the user's retained rate (written synchronously in handlePlaybackRateChange),
      // so re-apply it once the new call's metadata is ready. Reading the persisted
      // value avoids any dependency on Redux having committed the rate yet (QCER-885).
      const storedPlaybackRate = localStorage.getItem('audioPlaybackRate')
      if (storedPlaybackRate) {
        changeAudioPlaybackRate(parseFloat(storedPlaybackRate))
      }
    }

    addAudioEvent('loadedmetadata', durationListener)
    addAudioEvent('loadedmetadata', restorePlaybackRate)
    addAudioEvent('error', errorListener)

    if (!isAudioAvailable && isVideoAvailable) {
      addVideoEvent('loadedmetadata', durationListener)
    }

    startExpirationTracking()
    checkCorsSupport(audioUrl)

    const storedPlaybackRate = localStorage.getItem('audioPlaybackRate')
    if (storedPlaybackRate) {
      setPlaybackRate(parseFloat(storedPlaybackRate))
    }
    const storedSkipSilence = localStorage.getItem('skipSilence')
    if (storedSkipSilence) {
      setSkipSilence(getBooleanValueFromString(storedSkipSilence, false))
    }

    if (progress !== 0) {
      setProgress(progress)
    }

    // For Call Explorer initial search params
    const { timestamp: strTimestamp, eventId, autoplay } = parseCallExplorerParams(location)
    const timestamp = Number(strTimestamp)

    if (timestamp) {
      setProgress(timestamp)

      // we set this in EventViewer if there's a selected event
      if (!eventId) {
        dispatch(setHardSelectedEvent({ timestamp }))
      }

      if (autoplay) {
        setIsPlaying(true)
      }
    }

    return () => {
      removeAudioEvent('loadedmetadata', durationListener)
      removeAudioEvent('loadedmetadata', restorePlaybackRate)
      removeAudioEvent('error', errorListener)

      if (!isAudioAvailable && isVideoAvailable) {
        removeVideoEvent('loadedmetadata', durationListener)
      }

      stopProgressTracking()
      stopExpirationTracking()
    }
  }, [audioUrl])

  useEffect(() => {
    const onTimeUpdate = () => {
      handleUpdateClosestTranscriptBlock(audioRef.current.currentTime)
    }

    if (!isNil(audioRef.current)) {
      audioRef.current.addEventListener('timeupdate', onTimeUpdate)
    }

    return () => {
      if (!isNil(audioRef.current)) {
        audioRef.current.removeEventListener('timeupdate', onTimeUpdate)
      }
    }
  }, [transcriptType])

  // Opening the composer pauses playback. Only on the opening edge — closing it
  // leaves the player wherever the user left it rather than resuming for them.
  // Auto-scroll is already frozen for the whole interaction via
  // isCommentingActive below, which an open composer always implies.
  useEffect(() => {
    if (isComposingComment) {
      dispatch(pauseMediaPlayer())
    }
  }, [isComposingComment])

  useEffect(() => {
    const shouldScroll =
      isTranscriptActive &&
      transcriptEnableAutoScroll &&
      allowAutoScroll &&
      !isCommentingActive &&
      !isNil(transcriptBlockRef.current) &&
      !isNil(closestTranscriptIndexRef.current)

    if (shouldScroll) {
      handleScroll()
    }
    // isCommentingActive in deps: when a comment interaction ends the freeze lifts
    // and this re-runs, catching the transcript back up to the playback position.
  }, [closestTranscriptIndexRef.current, transcriptBlockRef.current, isCommentingActive])

  // Scroll to a focused comment's turn (clicking a card in the Transcript Comments
  // tab, or a deep link from the list in agent studio page). Uses the same container.scrollTo
  // path as handleScroll — scrollIntoView doesn't move this custom scroll
  // container. Runs after the on-mount playback scroll, so the focus wins.
  const focusedTurnStartMs = comments?.focusedTurnStartMs
  useEffect(() => {
    if (isNil(focusedTurnStartMs) || isNil(transcriptBlockRef.current)) {
      return
    }
    const index = transcriptBlocksFirstTimes.findIndex(
      (time) => Math.round(time * 1000) === focusedTurnStartMs
    )
    const container = transcriptBlockRef.current
    const block = container.childNodes?.[index]
    if (index >= 0 && block) {
      container.scrollTo({ top: block.offsetTop - container.offsetTop, behavior: 'smooth' })
    }
  }, [focusedTurnStartMs])

  return (
    <>
      {audioStatus === 'expired' && (
        <Banner
          data-testid="audio-expired"
          informative
          header="Audio for this call is not available"
          icon={<IconVolume3 />}
        >
          The audio recording is not available per your organization&apos;s data retention policies.
          The transcript and call events associated with this call will remain unchanged and
          accessible.
        </Banner>
      )}
      {audioError && audioStatus !== 'expired' && (
        <Banner
          data-testid="audio-not-available"
          informative
          header="Audio for this call is not available"
          icon={<IconVolume3 />}
        >
          Audio Player encountered an unexpected error during playback. Please refresh to continue
          listening. If issue persists please reach out to support.
        </Banner>
      )}
      {!audioError && (audioStatus === 'not_available' || audioStatus === 'unavailable') && (
        <Banner
          data-testid="audio-unavailable"
          informative
          header="Audio is not available for this call"
          icon={<IconVolume3 />}
        />
      )}
      <div
        className={classNames('media-player-container', {
          'without-transcript': !isTranscriptVisible,
          'video-minimized': isVideoAvailable && !isVideoMaximized,
        })}
      >
        {isVideoAvailable && (
          <VideoPlayer
            isPlaying={isPlaying}
            progress={progress}
            playbackRate={playbackRate}
            sources={videoUrls}
            onPlaying={videoOnPlaying}
            onPause={videoOnPause}
            onSeeked={videoOnSeeked}
          />
        )}
        <AudioPlayer
          callId={callId}
          loading={loading.mediaPlayerByCallId}
          isPlaying={isPlaying}
          duration={duration}
          progress={progress}
          playbackRate={playbackRate}
          volume={volume}
          audioUrl={audioUrl}
          isDisabled={playerIsDisabled}
          disabledTooltip={isComposingComment ? 'Finish your comment to use the player' : undefined}
          handleScrub={handleScrub}
          handleBackSkip={handleBackSkip}
          handleForwardSkip={handleForwardSkip}
          handleVolumeChange={handleVolumeChange}
          handlePlaybackRateChange={handlePlaybackRateChange}
          handleCloseDrawer={handleCloseDrawer}
          togglePlayState={togglePlayState}
          hideCloseButton={!handleHidePlayer}
          isTranscriptAvailable={isTranscriptAvailable}
          skipSilence={skipSilence}
          handleSkipSilenceChange={handleSkipSilenceChange}
          setProgress={setProgress}
          metadata={transcriptMetadata}
          showTranscriptButton={showTranscriptButton}
          hideShareButton={hideShareButton}
          useMinimalUi={useMinimalUi}
        />
      </div>
      {isTranscriptActive && (
        <div className="transcript-container">
          <Transcript
            metadata={transcriptMetadata}
            showTranscriptSelector={showTranscriptSelector}
            enableAutoScroll={transcriptEnableAutoScroll}
            handleTimestampClick={handleTimestampClick}
            hardSelectedEvent={hardSelectedEvent}
            showTranslateFeature={showTranslateFeature}
          />
        </div>
      )}
    </>
  )
}
